// TestimonialSection.js
import React, { useEffect, useState, useMemo } from "react";
import { Star, ChevronLeft, ChevronRight } from "lucide-react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Autoplay, Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

function normalizeImageField(fieldValue) {
  if (!fieldValue) return null;
  if (typeof fieldValue === "string") return fieldValue;
  if (typeof fieldValue === "object")
    return fieldValue.url || fieldValue?.sizes?.medium || null;
  return null;
}

function buildTestimonialsFromACF(acf = {}) {
  // Repeater support (if ever enabled)
  if (Array.isArray(acf?.testimonials) && acf.testimonials.length > 0) {
    return acf.testimonials.map((t, idx) => ({
      id: `rep-${idx}`,
      img: normalizeImageField(t.student_image),
      name: t.student_name || "Student",
      role: t.student_role || "",
      review: t.student_review || "",
      rating: Number(t.student_rating || 0),
    }));
  }

  // ✅ STRICT numbered field support
  const testimonialKeys = Object.keys(acf).filter((k) =>
    k.startsWith("student_name_")
  );

  return testimonialKeys
    .map((key) => {
      const id = key.replace("student_name_", "");
      return {
        id,
        img: normalizeImageField(acf[`student_image_${id}`]),
        name: acf[`student_name_${id}`],
        role: acf[`student_role_${id}`] || "",
        review: acf[`student_review_${id}`] || "",
        rating: Number(acf[`student_rating_${id}`] || 0),
      };
    })
    .filter(
      (t) => t.review && t.review.trim().length > 0
    );
}


export default function TestimonialSection() {
  const [acf, setAcf] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    fetch("https://easylearne.com/wp-json/acf/v3/pages/7")
      .then((res) => res.json())
      .then((json) => mounted && setAcf(json?.acf || {}))
      .catch(console.error)
      .finally(() => mounted && setLoading(false));

    return () => (mounted = false);
  }, []);

  const testimonials = useMemo(() => {
    if (!acf || typeof acf !== "object") return [];
    return buildTestimonialsFromACF(acf);
  }, [acf]);

  const safeTestimonials =
    testimonials.length > 0
      ? testimonials
      : [
          {
            id: "fb1",
            img: "https://i.pravatar.cc/100?img=32",
            name: "Sample User",
            role: "Sample Role",
            review: "This is a sample testimonial.",
            rating: 5,
          },
        ];

  if (loading)
    return (
      <section className="container py-12">
        <div className="text-center text-gray-500">Loading testimonials...</div>
      </section>
    );

  return (
    <section className="container py-10 px-4 relative">
      <div className="flex flex-col lg:flex-row gap-8 items-start">
         <div className="w-full lg:w-1/2">
      <h2 className="text-center text-3xl sm:text-4xl font-bold text-blue-700 mb-10">
        What Students Say
      </h2>

      <div className="relative max-w-5xl mx-auto">
        <button
          className="testimonial-prev hidden md:flex items-center justify-center absolute -left-8 top-1/2 -translate-y-1/2 p-3 bg-white rounded-full shadow z-20"
          aria-label="Previous"
        >
          <ChevronLeft className="w-5 h-5 text-blue-700" />
        </button>

        <button
          className="testimonial-next hidden md:flex items-center justify-center absolute -right-8 top-1/2 -translate-y-1/2 p-3 bg-white rounded-full shadow z-20"
          aria-label="Next"
        >
          <ChevronRight className="w-5 h-5 text-blue-700" />
        </button>
 <Swiper
  modules={[Navigation, Autoplay, Pagination]}
  navigation={{
    prevEl: ".testimonial-prev",
    nextEl: ".testimonial-next",
  }}
  autoplay={{ delay: 3200 }}
  loop={safeTestimonials.length > 2}
  spaceBetween={30}
  breakpoints={{
    320: { slidesPerView: 1 },
    768: { slidesPerView: 1.2 },
    1024: { slidesPerView: 2 },
  }}
>

       
          {safeTestimonials.map((t) => (
            <SwiperSlide key={t.id}>
              <div className="p-6 rounded-2xl bg-white border border-gray-200 h-[350px] md:h-[360px] flex flex-col items-center text-center">
                
                {/* Image - shadow removed */}
                {t.img ? (
                  <img
                    src={t.img}
                    className="w-20 h-20 rounded-full object-cover border-2 border-blue-500"
                    alt={t.name}
                  />
                ) : (
                  <div className="w-20 h-20 rounded-full bg-gray-200" />
                )}

                {/* Rating */}
                <div className="flex gap-1 mt-3 text-yellow-500">
                  {Array.from({ length: Math.round(t.rating) }).map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-yellow-500" />
                  ))}
                </div>

                <p className="text-gray-700 text-sm mt-4 italic flex-grow line-clamp-4">
                  "{t.review}"
                </p>

                <p className="font-semibold text-gray-900 mt-4">{t.name}</p>
                <p className="text-blue-600 text-sm">{t.role}</p>
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>

      <p className="text-center text-gray-500 mt-6 text-sm">
        {safeTestimonials.length} review{safeTestimonials.length > 1 ? "s" : ""}
      </p>
      </div>
      <div className="w-full lg:w-1/2">
        <iframe src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3685.838218825303!2d88.33194019999999!3d22.510252299999998!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3a0271d61422d285%3A0xb20e92db069310dd!2sEasylearn%20Educare%20%7C%20Study%20Abroad%2C%20UG%20PG%20Admissions!5e0!3m2!1sen!2sin!4v1784448675011!5m2!1sen!2sin" 
        width="600" title="map" height="450"  allowfullscreen="" loading="lazy" referrerpolicy="strict-origin-when-cross-origin"></iframe>
      </div>
      </div>
    </section>
  );
}
